import React from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditIcon from "@mui/icons-material/Edit";
import { Delete } from "@mui/icons-material";
import { mkConfig, generateCsv, download } from "export-to-csv";
import ToastComponent from "./ToastComponent";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";
import { useSelector } from "react-redux";
// ✅ CSV config
const csvConfig = mkConfig({
  fieldSeparator: ",",
  decimalSeparator: ".",
  useKeysAsHeaders: true,
});

// ✅ Helper: Export data to XLSX (with formatted summary rows)
const exportDataToXLSX = (data, fileName = "buy_success.xlsx") => {
  if (!data || data.length === 0) {
    // alert("No data to export!");
    return;
  }

  // ✅ Calculate totals
  const totalTransactions = data.length;
  const totalOrderAmount = data.reduce(
    (sum, row) => sum + (parseFloat(row.orderAmount) || 0),
    0,
  );

  // ✅ Convert JSON data to worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // ✅ Find where data ends (to append totals)
  const lastDataRow = data.length + 1; // Excel rows are 1-indexed

  // ✅ Add two summary rows exactly like your report
  XLSX.utils.sheet_add_aoa(
    worksheet,
    [
      ["", ""],
      ["Total no. of Transactions", totalTransactions],
      ["Total Order Amount", totalOrderAmount.toFixed(2)],
    ],
    { origin: `A${lastDataRow + 1}` },
  );

  // ✅ Optional: make header bold
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    if (worksheet[cellAddress]) {
      worksheet[cellAddress].s = { font: { bold: true } };
    }
  }

  // ✅ Create workbook & sheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "DSR Report");

  // ✅ Write file
  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName);
};

// ✅ Main Table Component
const CustomTable = ({
  columns,
  data,
  showRowAction = false,
  DataForExportAll,
  loading = false,
  date,
  permissionKey,
}) => {
  // console.log("date: in column ", date);
  if (!data) data = [];
  if (!DataForExportAll) DataForExportAll = data;

  const handleExportRows = (rows) => {
    const rowData = rows.map((row) => row.original);
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  //-------------logic for show export all data----------

  const rolePermissions = useSelector((state) => state.login.login.role);
  // console.log("[[[[[[[[[[[[]]]]]]]]]]]]]]]]]]]]]]]----rolepermision------",rolePermissions)
  const canExport = rolePermissions?.[permissionKey]?.export === 1;

  // console.log("=++++++++++", canExport)

  //-------------------$$----------------------------------

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(DataForExportAll);
    download(csvConfig)(csv);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const table = useMaterialReactTable({
    columns,
    data,
    state: { isLoading: loading },
    initialState: { density: "compact" },
    enableRowSelection: true,
    columnFilterDisplayMode: "popover",
    paginationDisplayMode: "pages",
    positionToolbarAlertBanner: "bottom",
    enableRowActions: showRowAction,
    positionActionsColumn: "last",
    enableGlobalFilter: true,
    maxSize: 100,

    muiTableHeadCellProps: {
      sx: () => ({
        background: "#DCDAFF",
        fontWeight: "normal",
      }),
    },

    renderRowActions: ({ row }) => (
      <Box display="flex" flexDirection="row" alignItems="center">
        <Tooltip title="Edit User">
          <IconButton>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton>
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        {/* Export All CSV */}
        {!isMobile && canExport && (
          <Button onClick={handleExportData} startIcon={<FileDownloadIcon />}>
            Export All Data
          </Button>
        )}

        {/* ✅ Export to XLSX */}
        {!isMobile && (
          <Button
            // startIcon={<FileDownloadIcon />}
            startIcon={<FaFileExcel />}
            onClick={() => {
              const selectedRows = table
                .getSelectedRowModel()
                .rows.map((r) => r.original);
              const exportData =
                selectedRows.length > 0 ? selectedRows : DataForExportAll;

              exportDataToXLSX(exportData, `buy_success_${date}.xlsx`);
            }}
          >
            Download DSR
          </Button>
        )}
      </Box>
    ),
  });

  return (
    <>
      {/* */}
      <MaterialReactTable table={table} />
    </>
  );
};

export default CustomTable;
